import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberField } from "@/components/ui/number-field";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { productosAPI, type Producto as ProductoAPI } from "@/lib/api";
import { filterProductSearchMatches, handleProductSearchHotkey, toNum } from "@/lib/utils";
import {
  buildComboLines,
  type ComboCapableLine,
  type ComboStockProduct,
  validateComboPicks,
} from "@/lib/comboLine";
import {
  formatStockTotalLabel,
  productoEsPersonalizado,
  productoStockActual,
  productoStockReservado,
} from "@/lib/ventaStockDisponible";

function asStockProduct(p: ProductoAPI): ComboStockProduct {
  return {
    id: p.id,
    codigo: p.codigo,
    nombre: p.nombre,
    precioFinal: toNum(p.precioFinal ?? p.precio_final),
    precio_final: p.precio_final,
    precioBase: toNum(p.precioBase ?? p.precio_base),
    esPersonalizado: p.esPersonalizado,
    ivaPorcentaje: p.ivaPorcentaje,
    iva_porcentaje: (p as ProductoAPI & { iva_porcentaje?: number }).iva_porcentaje,
  };
}

function ComboProductPicker({
  label,
  excludeId,
  value,
  onPick,
  priceSource = "venta",
}: {
  label: string;
  excludeId?: number;
  value: ComboStockProduct | null;
  onPick: (p: ComboStockProduct | null) => void;
  priceSource?: "venta" | "base";
}) {
  const [query, setQuery] = useState("");
  const [found, setFound] = useState<ProductoAPI[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!query.trim()) {
        setFound([]);
        return;
      }
      setSearching(true);
      try {
        const results = await productosAPI.list({
          search: query,
          esPersonalizado: false,
        });
        if (cancelled) return;
        setFound(
          (results || []).filter(
            (p) =>
              p.id &&
              p.id !== excludeId &&
              !productoEsPersonalizado(p),
          ),
        );
      } catch (e) {
        console.error(e);
        if (!cancelled) setFound([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, open, excludeId]);

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const filtered = filterProductSearchMatches(found, query);

  return (
    <div className="min-w-0 space-y-1" ref={wrapRef}>
      <Label>{label}</Label>
      {value ? (
        <div className="flex min-w-0 items-start justify-between gap-2 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 whitespace-normal break-words">
            <span className="text-xs text-muted-foreground mr-2">{value.codigo}</span>
            {value.nombre}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => {
              onPick(null);
              setQuery("");
              setFound([]);
            }}
          >
            Cambiar
          </Button>
        </div>
      ) : (
        <div className="relative min-w-0">
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Buscar producto de stock..."
            onKeyDown={(e) =>
              handleProductSearchHotkey(e, filtered.length, () => {
                const p = filtered[0];
                if (!p?.id) return;
                onPick(asStockProduct(p));
                setQuery("");
                setOpen(false);
              })
            }
          />
          {searching && (
            <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {open && query.trim() && (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto overflow-x-hidden rounded-lg border bg-background shadow-lg">
              {filtered.length === 0 && !searching ? (
                <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No hay productos de stock con ese criterio.
                </p>
              ) : (
                filtered.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="flex w-full min-w-0 items-start justify-between gap-2 border-b border-border px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted"
                    onClick={() => {
                      onPick(asStockProduct(p));
                      setQuery("");
                      setOpen(false);
                    }}
                  >
                    <span className="min-w-0 flex-1 whitespace-normal break-words">
                      <span className="mr-2 text-xs text-muted-foreground">{p.codigo}</span>
                      {p.nombre}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      $
                      {toNum(
                        priceSource === "base"
                          ? (p.precio_base ?? p.precioBase)
                          : (p.precio_final ?? p.precioFinal),
                      ).toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                      })}{" "}
                      | {formatStockTotalLabel(productoStockActual(p), productoStockReservado(p))}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ComboProductosDialog({
  open,
  onOpenChange,
  initialQty = 1,
  onAdd,
  priceSource = "venta",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialQty?: number;
  onAdd: (lines: ComboCapableLine[]) => void;
  /** Pedido a proveedor usa precio base. La venta usa precio final. */
  priceSource?: "venta" | "base";
}) {
  const [prodA, setProdA] = useState<ComboStockProduct | null>(null);
  const [prodB, setProdB] = useState<ComboStockProduct | null>(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (open) {
      setProdA(null);
      setProdB(null);
      setQty(Math.max(1, Math.floor(initialQty || 1)));
    }
  }, [open, initialQty]);

  const pickError = validateComboPicks(prodA, prodB);

  const confirm = () => {
    if (!prodA || !prodB) {
      toast.error("Elegí dos productos de stock.");
      return;
    }
    if (pickError) {
      toast.error(pickError);
      return;
    }
    try {
      onAdd(buildComboLines(prodA, prodB, qty, undefined, priceSource));
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo armar el combo.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid-cols-1 min-w-0 sm:max-w-[780px]">
        <DialogHeader className="min-w-0">
          <DialogTitle>Combinar productos</DialogTitle>
          <DialogDescription>
            Elegí dos productos de stock y una cantidad. Se agregan las dos
            líneas con la misma cantidad (no es un ítem personalizado ni un
            producto nuevo).
          </DialogDescription>
        </DialogHeader>
        <div className="min-w-0 space-y-4">
          <ComboProductPicker
            label="Producto A"
            excludeId={prodB?.id}
            value={prodA}
            onPick={setProdA}
            priceSource={priceSource}
          />
          <ComboProductPicker
            label="Producto B"
            excludeId={prodA?.id}
            priceSource={priceSource}
            value={prodB}
            onPick={setProdB}
          />
          <div className="space-y-1">
            <Label htmlFor="combo-qty">Cantidad (para ambos)</Label>
            <NumberField
              id="combo-qty"
              min={1}
              step={1}
              className="w-28 max-w-full"
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          </div>
        </div>
        <DialogFooter className="min-w-0 sm:justify-end">
          <div className="flex w-full min-w-0 flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="button" onClick={confirm} disabled={!!pickError}>
              Agregar combo
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
